class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const render = (state) => {
      const drawerHTML = state?.sections?.['cart-drawer'];
      if (!drawerHTML) {
        throw new Error('Cart drawer section was not returned by Shopify.');
      }

      const drawerInner = this.querySelector('.drawer__inner');
      if (!drawerInner) return;

      drawerInner.classList.contains('is-empty') && drawerInner.classList.remove('is-empty');
      this.classList.remove('is-empty');
      this.productId = state.id;

      const sectionElement = document.querySelector('#CartDrawer');
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(drawerHTML, '#CartDrawer');
      }

      const iconHTML = state?.sections?.['cart-icon-bubble'];
      const iconElement = document.getElementById('cart-icon-bubble');
      if (iconHTML && iconElement) {
        iconElement.innerHTML = this.getSectionInnerHTML(iconHTML, '.shopify-section');
      }

      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.open();
        window.NaiaCartReservation?.start();
      });
    };

    // Dawn's cart renderer expects bundled section HTML. This theme originally
    // rendered the drawer only as a snippet, so Shopify returned cart-drawer=null.
    // Fetch the section explicitly as a fallback so the drawer always receives
    // fresh server-rendered cart markup.
    if (!parsedState?.sections?.['cart-drawer']) {
      const root = window.Shopify?.routes?.root || '/';
      fetch(`${root}?section_id=cart-drawer`)
        .then((response) => {
          if (!response.ok) throw new Error(`Cart drawer section request failed (${response.status}).`);
          return response.text();
        })
        .then((html) => {
          parsedState.sections = parsedState.sections || {};
          parsedState.sections['cart-drawer'] = html;
          render(parsedState);
        })
        .catch((error) => {
          console.error('[Naïa] Cart drawer render error:', error);
        });
      return;
    }

    render(parsedState);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    if (!element) throw new Error(`Cart drawer selector not found: ${selector}`);
    return element.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
